import { useEffect, useState } from "react";
import WPMLogo from "@/components/WPMLogo";
import { useLang } from "@/lib/i18n";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { label: t("nav.brands"), id: "brands" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.contact"), id: "contact" },
  ];

  const go = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(var(--bg))]/85 backdrop-blur-xl border-b border-[hsl(var(--line))]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <a
          href="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <div className="transition-transform group-hover:rotate-[-3deg]">
            <WPMLogo size={30} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-[15px] tracking-tight">
              Web Print Media
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-mono">
              AI · Fintech · SaaS
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="nav-link text-sm font-medium"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <LangSwitch lang={lang} setLang={setLang} />
          <button
            data-testid="header-cta"
            onClick={() => go("contact")}
            className="group inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-sm font-medium hover:bg-[hsl(var(--accent))] transition-colors"
          >
            {t("nav.startProject")}
            <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 rounded-full border border-[hsl(var(--line))] flex items-center justify-center"
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
        </button>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-[hsl(var(--line))] bg-[hsl(var(--bg))]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => go(n.id)}
                className="text-left py-2 font-medium"
              >
                {n.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-2">
              <LangSwitch lang={lang} setLang={setLang} />
              <button
                onClick={() => go("contact")}
                data-testid="mobile-cta"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
              >
                {t("nav.startProject")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LangSwitch({ lang, setLang }) {
  return (
    <div
      data-testid="lang-switch"
      className="inline-flex items-center rounded-full border border-[hsl(var(--line))] bg-[hsl(var(--bg-elev))] p-0.5 font-mono text-[11px] uppercase tracking-[0.12em]"
    >
      {["en", "fr"].map((code) => (
        <button
          key={code}
          data-testid={`lang-${code}`}
          onClick={() => setLang(code)}
          className={`px-2.5 h-7 rounded-full transition-colors ${
            lang === code
              ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
              : "text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
